/**
 * Organization Billing Section
 *
 * Admin view for managing an organization's billing configuration.
 * Provides full CRUD capabilities for Stedi connection, billing identities, and enrollments.
 *
 * Uses admin routes (/v2/admin/orgs/{organizationId}/*) for all operations.
 * Matches functionality of the provider BillingSettingsPage.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  type BillingEntityAddressFormData,
  BillingEntityForm,
  type BillingEntityFormData,
  type CreateBillingEntityParams,
  initialBillingEntityFormData,
  useBillingEntitiesAdmin,
  useBillingEntityOperationsAdmin,
} from '@features/billing-entities';
import { useOrgReadinessAdmin } from '@features/billing-readiness';
import { EditBillingEntityModal } from '@features/billing-readiness/components/EditBillingEntityModal';
import { PayerSearchDropdown } from '@features/billing-readiness/components/PayerSearchDropdown';
import { SetupProgressIndicator } from '@features/billing-readiness/components/SetupProgressIndicator';
import { useEnrollAllRequiredTransactions } from '@features/billing-readiness/hooks/useEnrollAllRequiredTransactions';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import {
  getRequiredTransactionTypes,
  getTransactionTypeLabel,
} from '@features/billing-readiness/utils/enrollmentStatus';
import { useEnrollmentOperationsAdmin, useEnrollmentsAdmin } from '@features/enrollments';
import { usePayerSearch } from '@features/payer-search';
import { ServiceFacilitiesTab } from '@features/service-facilities';
import { useStediConnectionAdmin, useStediStatusAdmin } from '@features/stedi-connection';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { BillingEntityResponse } from '@store/generated/billing-entities';
import type {
  JotBillRoutingPayerResponse,
  AdminUpdateOrganizationRequest,
} from '@store/generated/organizations';
import type { PayerResponse } from '@store/generated/payers';
import { useSnackbar } from 'notistack';

import {
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  usePatchV2AdminOrganizationsByOrganizationIdMutation,
} from '../store/hooks';

import { OrganizationJotBillFeesSection } from './organization-jotbill-fees-section';
import { OrganizationRemittancesSection } from './organization-remittances-section';
import { OrganizationTakeConfigCard } from './organization-take-config-card';
import { OrganizationManagedBillingCardContainer } from '../containers/organization-managed-billing-card-container';
import { OrganizationTransactionsSection } from './organization-transactions-section';

type TabValue =
  | 'overview'
  | 'jotbill-routing'
  | 'identities'
  | 'facilities'
  | 'enrollments'
  | 'transactions'
  | 'remittances'
  | 'jotbill-fees';

interface TabPanelProps {
  children?: React.ReactNode;
  value: TabValue;
  activeTab: TabValue;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, activeTab }) => {
  if (value !== activeTab) {
    return null;
  }
  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

interface JotBillRoutingPayerSearchProps {
  disabled?: boolean;
  excludedPayerUuids: string[];
  onSelect: (payer: PayerResponse) => void;
}

function JotBillRoutingPayerSearch({ disabled, excludedPayerUuids, onSelect }: JotBillRoutingPayerSearchProps) {
  const theme = useTheme();
  const {
    searchTerm,
    setSearchTerm,
    payers,
    isLoading,
    isFetching,
  } = usePayerSearch(300);
  const [isOpen, setIsOpen] = useState(false);

  const availablePayers = payers.filter(payer => payer.id && !excludedPayerUuids.includes(payer.id));

  const handleSelect = (payer: PayerResponse) => {
    onSelect(payer);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        size="small"
        label="Add payer route"
        placeholder="Search payers by name or ID"
        value={searchTerm}
        onChange={event => {
          setSearchTerm(event.target.value);
          if (event.target.value.length >= 2) {
            setIsOpen(true);
          }
        }}
        onFocus={() => {
          if (searchTerm.length >= 2) {
            setIsOpen(true);
          }
        }}
        disabled={disabled}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null,
          },
        }}
      />
      {isOpen && searchTerm.length >= 2 && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            mt: 0.5,
            maxHeight: 300,
            overflow: 'auto',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: theme.shadows[8],
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : availablePayers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No available payers found
            </Typography>
          ) : (
            availablePayers.map(payer => (
              <Box
                key={payer.id || payer.stedi_id}
                onClick={() => handleSelect(payer)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:hover': { bgcolor: theme.palette.action.hover },
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Avatar
                  src={payer.avatar_url || undefined}
                  alt={payer.display_name}
                  sx={{ width: 32, height: 32 }}
                >
                  {payer.display_name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2">{payer.display_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {payer.primary_payer_id || payer.stedi_id}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}

interface JotBillRoutingPayerRowProps {
  payer?: JotBillRoutingPayerResponse;
  payerUuid: string;
  disabled?: boolean;
  onRemove: (payerUuid: string) => void;
}

function JotBillRoutingPayerRow({ payer, payerUuid, disabled, onRemove }: JotBillRoutingPayerRowProps) {
  const payerName = payer?.display_name || payerUuid;

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={payer?.avatar_url || undefined}
            alt={payerName}
            sx={{ width: 28, height: 28, fontSize: '0.8rem' }}
          >
            {payerName.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2">{payerName}</Typography>
            {payer?.primary_payer_id || payer?.stedi_id ? (
              <Typography variant="caption" color="text.secondary">
                {payer.primary_payer_id || payer.stedi_id}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          Primary payer UUID matches this payer
        </Typography>
      </TableCell>
      <TableCell>
        <Chip size="small" color="primary" label="Bill with JotPsych" />
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Remove payer route">
          <IconButton
            size="small"
            color="error"
            disabled={disabled}
            onClick={() => onRemove(payerUuid)}
            aria-label={`Remove JotBill route for ${payerName}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface OrganizationBillingSectionProps {
  organizationId: string;
}

export function OrganizationBillingSection({ organizationId }: OrganizationBillingSectionProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // Tab state
  const [currentTab, setCurrentTab] = useState<TabValue>('overview');

  // Dialog states
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [addEntityDialogOpen, setAddEntityDialogOpen] = useState(false);
  const [editEntityDialogOpen, setEditEntityDialogOpen] = useState(false);
  const [deleteEntityDialogOpen, setDeleteEntityDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<BillingEntityResponse | null>(null);

  // Form states
  const [apiKey, setApiKey] = useState('');
  const [accountLabel, setAccountLabel] = useState('');
  const [entityFormData, setEntityFormData] = useState<BillingEntityFormData>(initialBillingEntityFormData);
  const [formError, setFormError] = useState<string | null>(null);

  // Loading states
  const [syncingEntityId, setSyncingEntityId] = useState<string | null>(null);
  const [syncingEnrollmentId, setSyncingEnrollmentId] = useState<string | null>(null);
  const { enrollAllRequiredTransactions, enrollingPayerId } = useEnrollAllRequiredTransactions();

  // JotBill routing config
  const [jotbillDefaultMode, setJotbillDefaultMode] = useState<'off' | 'all_on'>('off');
  const [jotbillAutoEnableSelfPay, setJotbillAutoEnableSelfPay] = useState(false);
  const [jotbillPayerUuids, setJotbillPayerUuids] = useState<string[]>([]);
  const [jotbillPayersByUuid, setJotbillPayersByUuid] = useState<Record<string, JotBillRoutingPayerResponse>>({});
  const [updateOrganization, { isLoading: isUpdatingOrg }] = usePatchV2AdminOrganizationsByOrganizationIdMutation();

  // ============================================================================
  // Data Hooks
  // ============================================================================

  const { status: stediStatus, isConnected, isLoading: stediLoading, refetch: refetchStedi } = useStediStatusAdmin(
    organizationId,
  );
  const { data: organization, refetch: refetchOrganization } = useGetV2AdminOrganizationsByOrganizationIdQuery(
    { organizationId },
    { skip: !organizationId },
  );
  const { connect, connectLoading, verify, verifyLoading, disconnect, disconnectLoading } = useStediConnectionAdmin();
  const { readiness, isLoading: readinessLoading, refetch: refetchReadiness } = useOrgReadinessAdmin(organizationId);
  const { entities, isLoading: entitiesLoading, refetch: refetchEntities } = useBillingEntitiesAdmin(organizationId);
  const { enrollments, isLoading: enrollmentsLoading, refetch: refetchEnrollments } = useEnrollmentsAdmin(organizationId);
  const {
    create: createEntity,
    createLoading,
    remove: deleteEntity,
    removeLoading,
    sync: syncEntity,
    syncLoading,
    setDefault: setDefaultEntity,
    setDefaultLoading,
  } = useBillingEntityOperationsAdmin();
  const { 
    initiate: initiateEnrollment, 
    initiateLoading,
    sync: syncEnrollment, 
    syncAll, 
    syncAllLoading 
  } = useEnrollmentOperationsAdmin();

  // Get default entity for enrollments
  // First try to find explicitly default entity, then fall back to first synced entity
  const defaultEntity = entities.find(e => e.is_default && e.stedi_synced) || 
                        entities.find(e => e.stedi_synced);
  const defaultEntityId = defaultEntity?.billing_entity_id || null;

  const isLoading = stediLoading || readinessLoading || entitiesLoading || enrollmentsLoading;

  const hasConnection = isConnected;
  const hasIdentities = entities.length > 0;
  const hasEnrollments = enrollments.length > 0;
  const hasUnsyncedEntities = entities.some(e => !e.stedi_synced);

  useEffect(() => {
    const jotbillConfig = organization?.jotbill_routing_config;
    setJotbillDefaultMode(jotbillConfig?.default_mode ?? 'off');
    setJotbillAutoEnableSelfPay(jotbillConfig?.auto_enable_self_pay ?? false);
    setJotbillPayerUuids(jotbillConfig?.auto_enable_payer_uuids ?? []);
    setJotbillPayersByUuid(
      Object.fromEntries(
        (jotbillConfig?.auto_enable_payers ?? []).map(payer => [payer.payer_uuid, payer]),
      ),
    );
  }, [organization?.jotbill_routing_config]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: TabValue) => {
    setCurrentTab(newValue);
  }, []);

  const persistJotbillConfig = useCallback(async ({
    defaultMode,
    autoEnableSelfPay,
    payerUuids,
  }: {
    defaultMode: 'off' | 'all_on';
    autoEnableSelfPay: boolean;
    payerUuids: string[];
  }) => {
    const adminUpdateOrganizationRequest: AdminUpdateOrganizationRequest = {
      jotbill_routing_config: {
        default_mode: defaultMode,
        auto_enable_payer_uuids: payerUuids,
        auto_enable_self_pay: autoEnableSelfPay,
      },
    };

    try {
      await updateOrganization({
        organizationId,
        adminUpdateOrganizationRequest,
      }).unwrap();
      refetchOrganization();
    } catch {
      enqueueSnackbar('Failed to update JotBill routing', { variant: 'error' });
      refetchOrganization();
    }
  }, [
    organizationId,
    updateOrganization,
    enqueueSnackbar,
    refetchOrganization,
  ]);

  const handleAddJotbillPayer = useCallback((payer: PayerResponse) => {
    const payerUuid = payer.id;
    if (!payerUuid) {
      enqueueSnackbar('This payer cannot be added because it does not have a payer UUID', { variant: 'error' });
      return;
    }
    const nextPayerUuids = jotbillPayerUuids.includes(payerUuid)
      ? jotbillPayerUuids
      : [...jotbillPayerUuids, payerUuid];
    setJotbillPayerUuids(nextPayerUuids);
    setJotbillPayersByUuid(current => ({
      ...current,
      [payerUuid]: {
        payer_uuid: payerUuid,
        display_name: payer.display_name,
        primary_payer_id: payer.primary_payer_id,
        stedi_id: payer.stedi_id,
        avatar_url: payer.avatar_url,
      },
    }));
    persistJotbillConfig({
      defaultMode: jotbillDefaultMode,
      autoEnableSelfPay: jotbillAutoEnableSelfPay,
      payerUuids: nextPayerUuids,
    });
  }, [
    enqueueSnackbar,
    jotbillAutoEnableSelfPay,
    jotbillDefaultMode,
    jotbillPayerUuids,
    persistJotbillConfig,
  ]);

  const handleRemoveJotbillPayer = useCallback((payerUuid: string) => {
    const nextPayerUuids = jotbillPayerUuids.filter(currentPayerUuid => currentPayerUuid !== payerUuid);
    setJotbillPayerUuids(nextPayerUuids);
    setJotbillPayersByUuid(current => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([currentPayerUuid]) => currentPayerUuid !== payerUuid),
      );
      return next;
    });
    persistJotbillConfig({
      defaultMode: jotbillDefaultMode,
      autoEnableSelfPay: jotbillAutoEnableSelfPay,
      payerUuids: nextPayerUuids,
    });
  }, [
    jotbillAutoEnableSelfPay,
    jotbillDefaultMode,
    jotbillPayerUuids,
    jotbillPayersByUuid,
    persistJotbillConfig,
  ]);

  const handleJotbillDefaultModeChange = useCallback((defaultMode: 'off' | 'all_on') => {
    setJotbillDefaultMode(defaultMode);
    persistJotbillConfig({
      defaultMode,
      autoEnableSelfPay: jotbillAutoEnableSelfPay,
      payerUuids: jotbillPayerUuids,
    });
  }, [jotbillAutoEnableSelfPay, jotbillPayerUuids, persistJotbillConfig]);

  const handleJotbillSelfPayChange = useCallback((autoEnableSelfPay: boolean) => {
    setJotbillAutoEnableSelfPay(autoEnableSelfPay);
    persistJotbillConfig({
      defaultMode: jotbillDefaultMode,
      autoEnableSelfPay,
      payerUuids: jotbillPayerUuids,
    });
  }, [jotbillDefaultMode, jotbillPayerUuids, persistJotbillConfig]);

  const handleRefreshAll = useCallback(() => {
    refetchStedi();
    refetchReadiness();
    refetchEntities();
    refetchEnrollments();
    refetchOrganization();
  }, [refetchStedi, refetchReadiness, refetchEntities, refetchEnrollments, refetchOrganization]);

  // Connect to Stedi
  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setFormError('Please enter a Stedi API key');
      return;
    }

    setFormError(null);
    try {
      await connect({
        organizationId,
        connectStediRequest: {
          api_key: apiKey.trim(),
          account_label: accountLabel.trim() || undefined,
        },
      }).unwrap();
      enqueueSnackbar('Connected to Stedi successfully', { variant: 'success' });
      setConnectDialogOpen(false);
      setApiKey('');
      setAccountLabel('');
      refetchStedi();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to connect to Stedi');
      setFormError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleVerifyConnection = async () => {
    try {
      await verify({ organizationId }).unwrap();
      enqueueSnackbar('Stedi connection verified successfully', { variant: 'success' });
      refetchStedi();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to verify Stedi connection');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect({ organizationId }).unwrap();
      enqueueSnackbar('Disconnected from Stedi', { variant: 'success' });
      refetchStedi();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to disconnect from Stedi');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Billing Entity CRUD
  const handleOpenAddEntity = () => {
    setEntityFormData(initialBillingEntityFormData);
    setFormError(null);
    setAddEntityDialogOpen(true);
  };

  const handleOpenEditEntity = (entity: BillingEntityResponse) => {
    setSelectedEntity(entity);
    setEditEntityDialogOpen(true);
  };

  const handleOpenDeleteEntity = (entity: BillingEntityResponse) => {
    setSelectedEntity(entity);
    setDeleteEntityDialogOpen(true);
  };

  const handleCreateEntity = async () => {
    setFormError(null);
    const createPayload: CreateBillingEntityParams = {
      billing_npi: entityFormData.billing_npi.replace(/\D/g, ''),
      tax_id: entityFormData.tax_id.replace(/\D/g, ''),
      tax_id_type: entityFormData.tax_id_type,
      taxonomy_code: entityFormData.taxonomy_code.trim() || undefined,
      legal_name: entityFormData.legal_name.trim(),
      doing_business_as: entityFormData.doing_business_as.trim() || undefined,
      billing_address: {
        line1: entityFormData.billing_address.line1.trim(),
        line2: entityFormData.billing_address.line2.trim() || undefined,
        city: entityFormData.billing_address.city.trim(),
        state: entityFormData.billing_address.state.trim().toUpperCase(),
        zip: entityFormData.billing_address.zip.trim(),
      },
      contact_name: entityFormData.contact_name.trim() || undefined,
      contact_email: entityFormData.contact_email.trim(),
      contact_phone: entityFormData.contact_phone.trim(),
      is_default: entities.length === 0,
    };

    if (entityFormData.has_pay_to_address && entityFormData.pay_to_address.line1.trim()) {
      createPayload.pay_to_address = {
        line1: entityFormData.pay_to_address.line1.trim(),
        line2: entityFormData.pay_to_address.line2.trim() || null,
        city: entityFormData.pay_to_address.city.trim(),
        state: entityFormData.pay_to_address.state.trim().toUpperCase(),
        zip: entityFormData.pay_to_address.zip.trim(),
      };
    }

    try {
      await createEntity({
        organizationId,
        createBillingEntityRequest: createPayload,
      }).unwrap();
      enqueueSnackbar('Billing identity created successfully', { variant: 'success' });
      setAddEntityDialogOpen(false);
      refetchEntities();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to create billing identity');
      setFormError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDeleteEntity = async () => {
    if (!selectedEntity) return;
    try {
      await deleteEntity({
        organizationId,
        billingEntityId: selectedEntity.billing_entity_id,
      }).unwrap();
      enqueueSnackbar('Billing identity deleted successfully', { variant: 'success' });
      setDeleteEntityDialogOpen(false);
      setSelectedEntity(null);
      refetchEntities();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to delete billing identity');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleSyncEntity = async (billingEntityId: string) => {
    setSyncingEntityId(billingEntityId);
    try {
      await syncEntity({ organizationId, billingEntityId }).unwrap();
      enqueueSnackbar('Billing identity verified successfully', { variant: 'success' });
      refetchEntities();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to verify billing identity');
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    } finally {
      setSyncingEntityId(null);
    }
  };

  const handleSetDefaultEntity = async (billingEntityId: string) => {
    try {
      await setDefaultEntity({ organizationId, billingEntityId }).unwrap();
      enqueueSnackbar('Default billing identity updated', { variant: 'success' });
      refetchEntities();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to set default billing identity');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Enrollment Operations — enroll a payer for every transaction type it requires.
  const handleEnrollPayer = async (payer: PayerResponse) => {
    const payerId = payer.id;
    if (!defaultEntityId || !payerId) return;

    if (getRequiredTransactionTypes(payer).length === 0) {
      enqueueSnackbar(`${payer.display_name} doesn't require enrollment.`, { variant: 'info' });
      return;
    }

    const { succeeded, failed, errors } = await enrollAllRequiredTransactions(
      payer,
      transactionType =>
        initiateEnrollment({
          organizationId,
          initiateEnrollmentRequest: {
            billing_entity_id: defaultEntityId,
            payer_uuid: payerId,
            transaction_type: transactionType,
          },
        }).unwrap(),
    );

    if (failed.length === 0) {
      enqueueSnackbar(
        `Enrollment initiated for ${payer.display_name} (${succeeded
          .map(getTransactionTypeLabel)
          .join(', ')}). Usually takes 1-2 weeks for approval.`,
        { variant: 'success' },
      );
    } else if (succeeded.length > 0) {
      enqueueSnackbar(
        `Enrolled ${payer.display_name} for ${succeeded
          .map(getTransactionTypeLabel)
          .join(', ')}, but failed for ${failed
          .map(getTransactionTypeLabel)
          .join(', ')}: ${extractApiErrorMessage(errors[errors.length - 1])}`,
        { variant: 'warning', autoHideDuration: 8000 },
      );
    } else {
      enqueueSnackbar(
        extractApiErrorMessage(errors[errors.length - 1], 'Failed to initiate enrollment'),
        { variant: 'error' },
      );
    }
    if (succeeded.length > 0) {
      refetchEnrollments();
    }
  };

  const handleSyncEnrollment = async (enrollmentId: string) => {
    setSyncingEnrollmentId(enrollmentId);
    try {
      await syncEnrollment({ organizationId, enrollmentId }).unwrap();
      enqueueSnackbar('Enrollment status updated', { variant: 'success' });
      refetchEnrollments();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to sync enrollment');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSyncingEnrollmentId(null);
    }
  };

  const handleSyncAllEnrollments = async () => {
    try {
      await syncAll({ organizationId }).unwrap();
      enqueueSnackbar('All enrollments synced successfully', { variant: 'success' });
      refetchEnrollments();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to sync enrollments');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Form field change handlers
  const handleEntityFieldChange = (field: keyof BillingEntityFormData, value: string) => {
    setEntityFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressFieldChange = (field: keyof BillingEntityAddressFormData, value: string) => {
    setEntityFormData(prev => ({
      ...prev,
      billing_address: { ...prev.billing_address, [field]: value },
    }));
  };

  const handlePayToAddressFieldChange = (
    field: keyof BillingEntityAddressFormData,
    value: string,
  ) => {
    setEntityFormData(prev => ({
      ...prev,
      pay_to_address: { ...prev.pay_to_address, [field]: value },
    }));
  };

  // No Statsig tracking here — admin-v2 surfaces are exempt from product analytics.
  const handleTogglePayTo = (checked: boolean) => {
    setEntityFormData(prev => ({ ...prev, has_pay_to_address: checked }));
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Claim Submission
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshAll}
        >
          Refresh
        </Button>
      </Box>

      {/* Setup Progress Indicator */}
      <SetupProgressIndicator
        hasConnection={hasConnection}
        hasIdentities={hasIdentities}
        allIdentitiesVerified={hasIdentities && !hasUnsyncedEntities}
        hasRequiredEnrollments={hasEnrollments}
      />

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'transparent',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="jotbill-routing" label="BillWithJotPsych Routing" />
          <Tab value="identities" label="Billing Identities" />
          <Tab value="facilities" label="Service Facilities" />
          <Tab value="enrollments" label="Payer Enrollments" />
          <Tab value="transactions" label="Transactions" />
          <Tab value="remittances" label="Remittances" />
          <Tab value="jotbill-fees" label="JotBill Fees" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      <TabPanel value="overview" activeTab={currentTab}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Clearinghouse Connection Status */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isConnected ? (
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <ErrorIcon color="error" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Clearinghouse
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isConnected ? 'Connected' : 'Not connected'}
                </Typography>
                {isConnected && stediStatus?.account_label && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={stediStatus.account_label} size="small" color="primary" />
                    <Chip label={stediStatus.mode || 'SHARED'} size="small" variant="outlined" />
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {!isConnected ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => setConnectDialogOpen(true)}
                    >
                      Connect
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={verifyLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                        onClick={handleVerifyConnection}
                        disabled={verifyLoading}
                      >
                        Verify
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={handleDisconnect}
                        disabled={disconnectLoading}
                      >
                        Disconnect
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Identities Status */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {hasIdentities && !hasUnsyncedEntities ? (
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  ) : hasIdentities ? (
                    <ErrorIcon color="warning" sx={{ mr: 1 }} />
                  ) : (
                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Billing Identities
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {hasIdentities
                    ? `${entities.length} ${entities.length === 1 ? 'identity' : 'identities'}${hasUnsyncedEntities ? ' (setup required)' : ''}`
                    : 'None configured'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentTab('identities')}
                >
                  Manage
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Payer Enrollments Status */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {hasEnrollments ? (
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  ) : (
                    <ErrorIcon color="warning" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Payer Enrollments
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {hasEnrollments
                    ? `${enrollments.filter(e => e.status === 'LIVE').length} active`
                    : 'None required'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentTab('enrollments')}
                >
                  Manage
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* JotBill Take (participation fee) Config */}
          <Grid size={{ xs: 12, md: 4 }}>
            <OrganizationTakeConfigCard organizationId={organizationId} />
          </Grid>

          {/* Managed billing: independent of the take */}
          <Grid size={{ xs: 12, md: 4 }}>
            <OrganizationManagedBillingCardContainer
              key={organizationId}
              organizationId={organizationId}
            />
          </Grid>
        </Grid>

        {!hasConnection && !hasIdentities && !hasEnrollments && (
          <Alert severity="info">
            <Typography variant="body2">
              This organization has not configured claim submission yet.
            </Typography>
          </Alert>
        )}
      </TabPanel>

      {/* BillWithJotPsych Routing Tab */}
      <TabPanel value="jotbill-routing" activeTab={currentTab}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  BillWithJotPsych Routing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Controls which patients are auto-flagged for JotPsych billing. Per-patient overrides always take priority.
                </Typography>
              </Box>
              <Chip
                size="small"
                color={isUpdatingOrg ? 'default' : 'success'}
                label={isUpdatingOrg ? 'Saving...' : 'Autosaves'}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <JotBillRoutingPayerSearch
                excludedPayerUuids={jotbillPayerUuids}
                onSelect={handleAddJotbillPayer}
                disabled={isUpdatingOrg}
              />
            </Box>

            <TableContainer>
              <Table size="small" aria-label="BillWithJotPsych routing">
                <TableHead>
                  <TableRow>
                    <TableCell>Route</TableCell>
                    <TableCell>Match</TableCell>
                    <TableCell>Behavior</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Organization default
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        Patients without a payer/self-pay match
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 320 }}>
                        <InputLabel id="jotbill-default-mode-label">Default mode</InputLabel>
                        <Select
                          labelId="jotbill-default-mode-label"
                          label="Default mode"
                          value={jotbillDefaultMode}
                          disabled={isUpdatingOrg}
                          onChange={event => {
                            handleJotbillDefaultModeChange(
                              event.target.value === 'all_on' ? 'all_on' : 'off',
                            );
                          }}
                        >
                          <MenuItem value="off">Off — patients must be individually enabled</MenuItem>
                          <MenuItem value="all_on">All on — all patients bill with JotPsych by default</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right">
                      <Chip size="small" label="Fallback" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Self-pay
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        Client is self-pay
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={jotbillAutoEnableSelfPay}
                            disabled={isUpdatingOrg}
                            onChange={event => handleJotbillSelfPayChange(event.target.checked)}
                          />
                        }
                        label="Bill with JotPsych"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip size="small" label="Rule" />
                    </TableCell>
                  </TableRow>
                  {jotbillPayerUuids.map(payerUuid => (
                    <JotBillRoutingPayerRow
                      key={payerUuid}
                      payer={jotbillPayersByUuid[payerUuid]}
                      payerUuid={payerUuid}
                      disabled={isUpdatingOrg}
                      onRemove={handleRemoveJotbillPayer}
                    />
                  ))}
                  {jotbillPayerUuids.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          No payer-specific routes configured.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Billing Identities Tab */}
      <TabPanel value="identities" activeTab={currentTab}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Billing Identities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Each identity represents an NPI and Tax ID combination for billing
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddEntity}
              >
                Add Identity
              </Button>
            </Box>

            {/*
              Stedi is a sync-time requirement, not a create-time one. The backend
              create-entity path is database-only (NPI/Tax ID/legal name); Stedi is
              only reached when verifying an identity (sync-to-stedi). So adding an
              identity is always allowed here — this just flags that verification
              will need a Stedi connection. (Eligibility-only orgs never verify:
              their checks read the persisted NPI directly. See GDN-3034.)
            */}
            {!hasConnection && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You can add billing identities now. Connecting to Stedi is only
                required to verify an identity before submitting claims.
              </Alert>
            )}

            {entities.length > 0 ? (
              <>
                {hasUnsyncedEntities && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Some billing identities need to be verified with the clearinghouse.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {entities.map(entity => (
                    <Box
                      key={entity.billing_entity_id}
                      sx={{
                        p: 2.5,
                        border: '1px solid',
                        borderColor: theme.palette.grey[200],
                        borderRadius: 2,
                        bgcolor: theme.palette.background.paper,
                        '&:hover': {
                          borderColor: theme.palette.grey[300],
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {entity.legal_name}
                          </Typography>
                          {entity.doing_business_as && (
                            <Typography variant="body2" color="text.secondary">
                              DBA: {entity.doing_business_as}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              NPI: {entity.billing_npi}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Tax ID: {entity.tax_id_masked} ({entity.tax_id_type})
                            </Typography>
                            {/* No "same as billing" counterpart — the chip's absence is that. */}
                            {entity.pay_to_address && (
                              <Chip label="Pay-to address set" size="small" variant="soft" />
                            )}
                          </Box>
                          {entity.stedi_provider_id && (
                            <Typography variant="caption" color="text.secondary" sx={{display: "block",  mt: 0.5 }}>
                              Stedi Provider ID: {entity.stedi_provider_id}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {entity.is_default ? (
                            <Chip label="Default" size="small" color="primary" variant="outlined" />
                          ) : (
                            <Tooltip title="Set as default billing identity">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSetDefaultEntity(entity.billing_entity_id)}
                                disabled={setDefaultLoading}
                                sx={{ textTransform: 'none', minWidth: 'auto' }}
                              >
                                Set Default
                              </Button>
                            </Tooltip>
                          )}
                          <Chip
                            label={entity.stedi_synced ? 'Ready' : 'Setup Required'}
                            size="small"
                            color={entity.stedi_synced ? 'success' : 'warning'}
                          />

                          {!entity.stedi_synced && (
                            <Tooltip
                              title={
                                hasConnection
                                  ? 'Verify billing identity'
                                  : 'Connect to Stedi to verify this billing identity'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSyncEntity(entity.billing_entity_id)}
                                  disabled={
                                    !hasConnection ||
                                    syncLoading ||
                                    syncingEntityId === entity.billing_entity_id
                                  }
                                >
                                  {syncingEntityId === entity.billing_entity_id ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <SyncIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditEntity(entity)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteEntity(entity)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {entity.stedi_synced && entity.stedi_synced_at && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                          Verified: {new Date(entity.stedi_synced_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Alert severity="info">
                <Typography variant="body2">
                  No billing identities configured. Click "Add Identity" to create one.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Service Facilities Tab */}
      <TabPanel value="facilities" activeTab={currentTab}>
        <ServiceFacilitiesTab />
      </TabPanel>

      {/* Payer Enrollments Tab */}
      <TabPanel value="enrollments" activeTab={currentTab}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Payer Enrollments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Some insurance companies require enrollment before accepting electronic claims. Search for a payer to check if enrollment is required.
                </Typography>
              </Box>
              {enrollments.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={syncAllLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                  onClick={handleSyncAllEnrollments}
                  disabled={syncAllLoading}
                >
                  {syncAllLoading ? 'Syncing...' : 'Sync All'}
                </Button>
              )}
            </Box>

            {/* Payer Search */}
            <Box sx={{ mb: 3 }}>
              <PayerSearchDropdown
                onEnroll={handleEnrollPayer}
                isEnrolling={initiateLoading}
                enrollingPayerId={enrollingPayerId}
                placeholder="Search for a payer to start enrollment..."
              />
              {!defaultEntityId && entities.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Set a default billing identity to enable enrollment. Go to the Billing Identities tab to set one as default.
                  </Typography>
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Payer Search */}
            {defaultEntityId && (
              <Box sx={{ mb: 3 }}>
                <PayerSearchDropdown
                  onEnroll={handleEnrollPayer}
                  isEnrolling={initiateLoading}
                  enrollingPayerId={enrollingPayerId}
                />
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Active Enrollments List */}
            {enrollments.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  My Enrollments {enrollments.length > 0 && `(${enrollments.length})`}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {enrollments.map(enrollment => {
                    const statusColor = enrollment.status === 'LIVE' ? 'success'
                      : enrollment.status === 'PENDING' || enrollment.status === 'PROVIDER_ACTION_REQUIRED' || enrollment.status === 'STEDI_ACTION_REQUIRED' ? 'warning'
                      : enrollment.status === 'PROVISIONING' || enrollment.status === 'DRAFT' ? 'info'
                      : enrollment.status === 'REJECTED' ? 'error'
                      : 'default';

                    const canSync = enrollment.status !== 'LIVE' && enrollment.status !== 'REJECTED';
                    const isSyncing = syncingEnrollmentId === enrollment.stedi_transaction_enrollment_id;

                    return (
                      <Box
                        key={enrollment.stedi_transaction_enrollment_id}
                        sx={{
                          p: 2,
                          border: `1px solid ${theme.palette.grey[200]}`,
                          borderRadius: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {enrollment.payer_name || enrollment.transaction_type}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {enrollment.transaction_type} • ID: {enrollment.stedi_transaction_enrollment_id}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={enrollment.status}
                            size="small"
                            color={statusColor}
                          />
                          {canSync && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
                              onClick={() => handleSyncEnrollment(enrollment.stedi_transaction_enrollment_id)}
                              disabled={isSyncing}
                            >
                              {isSyncing ? 'Syncing...' : 'Sync'}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Empty states */}
            {enrollments.length === 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  No payer enrollments configured. Most claims can be submitted without enrollment. Search for a payer above to check if enrollment is required.
                </Typography>
              </Alert>
            )}

            {entities.length === 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  No billing identities configured. Add a billing identity first to manage payer enrollments.
                </Typography>
              </Alert>
            )}

            {!hasConnection && (
              <Alert severity="warning">
                <Typography variant="body2">
                  Connect to Stedi first to manage payer enrollments.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Transactions Tab */}
      <TabPanel value="transactions" activeTab={currentTab}>
        <OrganizationTransactionsSection organizationId={organizationId} isConnected={isConnected} />
      </TabPanel>

      {/* Remittances Tab */}
      <TabPanel value="remittances" activeTab={currentTab}>
        <OrganizationRemittancesSection organizationId={organizationId} isConnected={isConnected} />
      </TabPanel>

      {/* JotBill Fees Tab */}
      <TabPanel value="jotbill-fees" activeTab={currentTab}>
        <OrganizationJotBillFeesSection organizationId={organizationId} />
      </TabPanel>

      {/* ================================================================== */}
      {/* DIALOGS */}
      {/* ================================================================== */}

      {/* Connect to Stedi Dialog */}
      <Dialog open={connectDialogOpen} onClose={() => setConnectDialogOpen(false)} fullWidth maxWidth={"sm"}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Connect to Stedi</span>
            <IconButton edge="end" onClick={() => setConnectDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter the organization's Stedi API key to connect to the clearinghouse.
          </Typography>
          <TextField
            label="Stedi API Key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            fullWidth
            required
            autoFocus
            sx={{ mb: 2 }}
            helperText="The API key from the Stedi app installation"
          />
          <TextField
            label="Account Label (optional)"
            value={accountLabel}
            onChange={(e) => setAccountLabel(e.target.value)}
            fullWidth
            helperText="For identification, e.g., clinic name"
          />
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={connectLoading}
            startIcon={connectLoading ? <CircularProgress size={20} /> : undefined}
          >
            {connectLoading ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Billing Entity Dialog */}
      <Dialog open={addEntityDialogOpen} onClose={() => setAddEntityDialogOpen(false)} fullWidth maxWidth={"md"}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Add Billing Identity</span>
            <IconButton edge="end" onClick={() => setAddEntityDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <BillingEntityForm
            data={entityFormData}
            onChange={handleEntityFieldChange}
            onAddressChange={handleAddressFieldChange}
            onPayToAddressChange={handlePayToAddressFieldChange}
            onTogglePayTo={handleTogglePayTo}
          />
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEntityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateEntity}
            disabled={createLoading}
            startIcon={createLoading ? <CircularProgress size={20} /> : undefined}
          >
            {createLoading ? 'Creating...' : 'Create Identity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Billing Entity Dialog (shared modal) */}
      {selectedEntity && (
        <EditBillingEntityModal
          open={editEntityDialogOpen}
          onClose={() => {
            setEditEntityDialogOpen(false);
            setSelectedEntity(null);
          }}
          entity={selectedEntity}
          organizationId={organizationId}
          onUpdated={() => {
            refetchEntities();
            refetchReadiness();
          }}
        />
      )}

      {/* Delete Billing Entity Dialog */}
      <Dialog open={deleteEntityDialogOpen} onClose={() => setDeleteEntityDialogOpen(false)} fullWidth maxWidth={"sm"}>
        <DialogTitle>Delete Billing Identity</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the billing identity "{selectedEntity?.legal_name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEntityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteEntity}
            disabled={removeLoading}
            startIcon={removeLoading ? <CircularProgress size={20} /> : undefined}
          >
            {removeLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
